<template>
  <Dialog :open="props.show" @update:open="onClose">
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Cancel Order</DialogTitle>
      </DialogHeader>
      <SectionDivider className="my-4" />
      <div>
        <p>The customer will be notified if this order is cancelled. Are you sure you want to cancel?</p>
        <SectionDivider className="my-4" />
        <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
        <Button @click="onCancelOrder" variant="primary">Cancel Order</Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
<script setup lang="ts">
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import type { Order } from '@/models/settlements';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { useMerchantStore } from '@/stores/merchants';
import { useOrderStore } from '@/stores/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { updateSuspendedOrder } from '@/utils/order.utils';
import { ref } from 'vue';
import type { ModelCancellationPolicy, ModelOrderResponse } from '@layup/api-sdk';

const emit = defineEmits(['update:show']);

const props = defineProps<{
  show: boolean;
  order?: ModelOrderResponse;
}>();

const merchantStore = useMerchantStore();
const orderStore = useOrderStore();
const errorMessage = ref('');
const onClose = () => {
  errorMessage.value = '';
  emit('update:show');
};
const onCancelOrder = async () => {
  try {
    errorMessage.value = '';
    const cancellationTerms: ModelCancellationPolicy = {
      name: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.name || 'Combination',
      definition: {
        deposit: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.deposit,
        percentage: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.percentage,
        flatFee: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.flatFee,
        percentageShouldUseTotalPaid:
          merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.percentageShouldUseTotalPaid,
        includeIncurredOrderFees:
          merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.includeIncurredOrderFees,
      },
    };
    const orderToUpdate: Order = {
      _id: props.order?._id || '',
      reference: props.order?.reference || '',
      state: 'CANCELLING',
      initiator: { _id: merchantStore.merchant?._id || '', name: merchantStore.merchant?.name || '' },
    };

    await updateSuspendedOrder(orderToUpdate?._id!, orderToUpdate, cancellationTerms);

    const updatedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(orderStore.order?._id!);
    orderStore.updateOrder(updatedOrder);
    emit('update:show');
    addMetric('cancellation-modal', { status: 'success  ', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('cancellation-modal', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('cancellation-modal', { status: 'error', channel: 'merchant' });
    }
    errorMessage.value = `Failed to activate order: ${error}`;
  }
};
</script>
